import React, { RefObject } from 'react';
import { TaskStatus } from '../task/TaskNode';

/** Node data for minimap rendering */
interface MinimapNode {
  id: string;
  name: string;
  x: number;
  y: number;
  status: TaskStatus;
}

/** Edge data for minimap rendering */
interface MinimapEdge {
  from: string;
  to: string;
  label?: string;
}

/** Viewport indicator position and size */
interface MinimapViewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface StreamGraphMinimapProps {
  /** All nodes in the graph */
  nodes: MinimapNode[];
  /** All edges in the graph */
  edges: MinimapEdge[];
  /** Currently selected node ID */
  selectedNode: string | null;
  /** Viewport indicator bounds */
  viewport: MinimapViewport;
  /** Click handler for panning to location */
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Node click handler for panning to node */
  onNodeClick: (nodeId: string, e: React.MouseEvent) => void;
}

/**
 * Calculate minimap viewport indicator position.
 * Converts graph coordinates to minimap coordinates.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getMinimapViewport(
  containerRef: RefObject<HTMLDivElement>,
  scale: number,
  panOffset: { x: number; y: number }
): MinimapViewport {
  if (!containerRef.current) {
    return { x: 0, y: 0, width: 50, height: 30 };
  }
  const container = containerRef.current.getBoundingClientRect();
  const minimapScaleX = 12;
  const minimapScaleY = 8;
  const padding = 16;

  // Viewport size in minimap coordinates
  const viewportWidth = container.width / scale / minimapScaleX;
  const viewportHeight = container.height / scale / minimapScaleY;

  // Viewport position: convert panOffset to minimap coordinates
  const viewportX = -panOffset.x / minimapScaleX + padding;
  const viewportY = -panOffset.y / minimapScaleY + padding;

  return {
    x: viewportX,
    y: viewportY,
    width: viewportWidth,
    height: viewportHeight
  };
}

/**
 * Get node status color for minimap rendering.
 */
function getNodeColor(status: TaskStatus, selected: boolean): string {
  const baseClasses = (() => {
    switch (status) {
      case 'completed':
        return 'bg-success-500';
      case 'running':
        return 'bg-primary-500';
      case 'attention':
        return 'bg-warning-500';
      case 'sleeping':
        return 'bg-purple-500';
      case 'waiting':
        return 'bg-amber-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-surface-500 opacity-50';
      default:
        return 'bg-surface-400';
    }
  })();

  return `${baseClasses} ${selected ? 'ring-2 ring-white' : ''}`;
}

/**
 * Interactive minimap component showing graph overview.
 * Displays nodes, edges, and viewport indicator with pan-to-click support.
 */
export function StreamGraphMinimap({
  nodes,
  edges,
  selectedNode,
  viewport,
  onClick,
  onNodeClick,
}: StreamGraphMinimapProps) {
  return (
    <div
      className="minimap absolute bottom-6 left-6 w-[200px] h-[120px] bg-surface-800/95 backdrop-blur border border-surface-500 rounded-lg shadow-xl overflow-hidden opacity-80 hover:opacity-100 transition-opacity cursor-pointer z-20"
      onClick={onClick}
    >
      <div className="w-full h-full relative p-4">
        {/* Viewport indicator */}
        <div
          className="absolute border-2 border-primary-500 bg-primary-500/10 rounded-sm pointer-events-none"
          style={{
            left: viewport.x,
            top: viewport.y,
            width: viewport.width,
            height: viewport.height
          }}
        />

        {/* Edges */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {edges.map((edge, i) => {
            const start = nodes.find(n => n.id === edge.from);
            const end = nodes.find(n => n.id === edge.to);
            if (!start || !end) return null;
            return (
              <line
                key={i}
                x1={start.x / 12 + 16}
                y1={start.y / 8 + 16}
                x2={end.x / 12 + 16}
                y2={end.y / 8 + 16}
                className="stroke-surface-400"
                strokeWidth="1"
              />
            );
          })}
        </svg>

        {/* Nodes */}
        {nodes.map(n => (
          <div
            key={n.id}
            onClick={e => onNodeClick(n.id, e)}
            className={`absolute w-3 h-1.5 rounded-sm transition-all cursor-pointer hover:scale-150 ${getNodeColor(n.status, selectedNode === n.id)}`}
            style={{
              left: n.x / 12 + 16,
              top: n.y / 8 + 16
            }}
            title={n.name}
          />
        ))}
      </div>
    </div>
  );
}
